import clsx from "clsx";
import { motion } from "motion/react";
import { useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/ui/alert/Alert";
import { Btn } from "@/ui/btn/Btn";
import { Checkbox } from "@/ui/checkbox/Checkbox";
import { Dialog } from "@/ui/dialog/Dialog";
import { Icon } from "@/ui/icon/Icon";
import { calculatorConversionSubmit } from "../../../events/events";
import { useCalculator } from "../../../hooks/useCalculator";
import { useCalculatorPaymentMethod } from "../../../hooks/useCalculatorPaymentMethod";
import { useCalculatorPaymentSubmit } from "../../../hooks/useCalculatorPaymentSubmit";
import type { Calc } from "../../../types";
import { CalculatorMessage } from "../../calculator-message/CalculatorMessage";
import { CalculatorPaymentMethod } from "../../calculator-payment-method/CalculatorPaymentMethod";
import styles from "./CalculatorPaymentMethodDialog.module.css";

export const CalculatorPaymentMethodDialog = () => {
  const setPaymentMethodDialogOpen = useCalculator(
    (state) => state.setPaymentMethodDialogOpen,
  );

  const { data, error, isLoading, isValidating } = useCalculatorPaymentMethod();

  const onClose = () => {
    setPaymentMethodDialogOpen(false);
  };

  return (
    <Dialog className={styles.dialog} size="lg" onClose={onClose}>
      <Dialog.Header>
        <Dialog.Title>
          <Trans>Select Payment Method</Trans>
        </Dialog.Title>
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Group>
          {error !== undefined && (
            <Alert appearance="orange" text={error.message} />
          )}
          {(isLoading === true || isValidating === true) && <DialogLoader />}
          {data !== undefined && <PaymentMethodForm {...data} />}
        </Dialog.Group>
      </Dialog.Body>
    </Dialog>
  );
};

interface PaymentMethodInfoProps {
  info: Calc.Api.PaymentMethodsResponse["info"];
}

const PaymentMethodInfo: React.FC<PaymentMethodInfoProps> = ({ info }) => {
  return (
    <div className={styles.info}>
      {info.map((item, index) => (
        <CalculatorMessage key={index} {...item} />
      ))}
    </div>
  );
};

const PaymentMethodForm: React.FC<Calc.Api.PaymentMethodsResponse> = ({
  paymentMethods,
  info,
}) => {
  const { t } = useTranslation();
  const links = useCalculator((state) => state.links);
  const [activePaymentMethod, setActivePaymentMethod] = useState(() => {
    return paymentMethods.find((paymentMethod) => paymentMethod.active);
  });
  const [isTermsConfirmed, setTermsConfirmed] = useState(false);
  const termsInputId = useId();

  const isSubmitAvailable =
    isTermsConfirmed === true && activePaymentMethod !== undefined;

  const { data, error, trigger, isMutating } =
    useCalculatorPaymentSubmit(activePaymentMethod);

  return (
    <motion.div
      className={styles.form}
      initial={{
        opacity: 0,
        height: 200,
      }}
      animate={{
        opacity: 1,
        height: "auto",
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <div className={styles.list}>
        {paymentMethods.map((paymentMethod, index) => (
          <CalculatorPaymentMethod
            key={index}
            {...paymentMethod}
            active={paymentMethod === activePaymentMethod}
            onChange={() => setActivePaymentMethod(paymentMethod)}
          />
        ))}
      </div>
      <div className={styles.terms}>
        <div className={styles.confirm}>
          <Checkbox
            id={termsInputId}
            checked={isTermsConfirmed}
            highlight={isTermsConfirmed === false}
            onChange={setTermsConfirmed}
          />
          <label className={styles.label} htmlFor={termsInputId}>
            <Trans>I confirm I have read and accepted:</Trans>
          </label>
        </div>
        <div className={styles.termsList}>
          <div
            className={clsx(
              styles.termsItem,
              isTermsConfirmed && styles.active,
            )}
          >
            <Icon className={styles.termsIcon} name="check_mark_24px_filled" />
            <a
              className={styles.link}
              href={links.terms.privacyPolicy}
              target="_blank"
              rel="noreferrer"
            >
              <Trans>Refund & Cancellation Policy</Trans>
            </a>
          </div>
          <div
            className={clsx(
              styles.termsItem,
              isTermsConfirmed && styles.active,
            )}
          >
            <Icon className={styles.termsIcon} name="check_mark_24px_filled" />
            <a
              className={styles.link}
              href={links.terms.termsOfService}
              target="_blank"
              rel="noreferrer"
            >
              <Trans>Terms of Service</Trans>
            </a>
          </div>
        </div>
      </div>
      <PaymentMethodInfo info={info} />
      {error !== undefined && (
        <Alert appearance="orange" text={error.message} />
      )}
      <Btn
        text={t("Continue to payment")}
        endIcon="go_24px_filled"
        color="primary"
        size="lg"
        disabled={isSubmitAvailable === false || data !== undefined}
        loading={isMutating}
        onClick={() => {
          calculatorConversionSubmit();
          trigger();
        }}
      />
    </motion.div>
  );
};

const DialogLoader = () => {
  return (
    <div className={styles.loader}>
      <Icon name="preloader_40px" size={80} />
      <div className={styles.loaderText}>
        <Trans>Loading...</Trans>
      </div>
    </div>
  );
};
